import Input from 'components/Globals/Base/Input';
import { DropdownList, Option } from 'components/Globals/Base/Dropdown';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InputProps } from 'components/Globals/Base/Input/types';
import useClickOutside from 'hooks/useClickOutside';
import * as S from './AutocompleteInputStyles';

export interface AutocompleteInputProps extends Omit<InputProps, 'onChange'> {
  options: string[] | Option[];
  selectedOption: string;
  onOptionClick: (opt: string) => void;
}

const AutocompleteInput = ({ options, selectedOption, onOptionClick, ...inputProps }: AutocompleteInputProps) => {
  const [option, setOption] = useState<Option>({ label: '', value: '' });
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const firstItemRef = useRef<HTMLLIElement>(null);
  const lastItemRef = useRef<HTMLLIElement>(null);

  const optionsList = useMemo(
    () => options.map((opt) => (typeof opt === 'string' ? { label: opt, value: opt } : opt)),
    [options],
  );

  const resetToSelectedOption = useCallback(() => {
    const searchedOpt = optionsList.find((opt) => opt.value === selectedOption) || { label: '', value: '' };
    setOption(searchedOpt);
  }, [optionsList, selectedOption]);

  const handleOnBlur = () => {
    if (option.value === '') onOptionClick(option.value);
    else resetToSelectedOption();
    setShowOptions(false);
  };

  useClickOutside(autocompleteRef, () => {
    handleOnBlur();
  });

  const getOptions = () => {
    if (showAllOptions) return optionsList;
    return optionsList.filter((opt) => opt.value.toLowerCase().includes(option.value.toLowerCase()));
  };

  const handleOptionClick = (opt: Option) => {
    setOption(opt);
    onOptionClick(opt.value);
    setShowOptions(false);
  };

  const handleOnChange = (val: string) => {
    // options list should be shown when changing input value if it's closed (ex. it's closed after selecting an option with keyboard navigation)
    if (!showOptions) {
      setShowOptions(true);
    }

    if (showAllOptions) setShowAllOptions(false);
    setOption({ label: val, value: val });
  };

  const handleOnFocus = () => {
    setShowOptions(true);
    setShowAllOptions(true); // when clicking on the input we always want to show all options
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        // open the options list on arrow down key if it's closed (ex. after selecting an option with keyboard navigation)
        if (!showOptions) {
          setShowOptions(true);
          setShowAllOptions(true);
        }

        if (firstItemRef.current) {
          firstItemRef.current.focus();
          event.preventDefault();
        }
        break;
      case 'ArrowUp':
        if (lastItemRef.current) {
          lastItemRef.current.focus();
          event.preventDefault();
        }
        break;
      case 'Tab':
        handleOnBlur();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    // this will reset the input value when selectedOption is reset to initial value outside of this component
    if (!showOptions && option.value !== selectedOption) {
      resetToSelectedOption();
    }
  }, [option.value, resetToSelectedOption, selectedOption, showOptions]);

  return (
    <S.AutocompleteWrapper ref={autocompleteRef}>
      <Input
        {...inputProps}
        value={option.label}
        onChange={handleOnChange}
        onFocus={handleOnFocus}
        onKeyDown={handleOnKeyDown}
        ref={inputRef}
      />
      <DropdownList
        isOpen={showOptions}
        onOptionClick={handleOptionClick}
        list={getOptions()}
        inputRef={inputRef}
        firstItemRef={firstItemRef}
        lastItemRef={lastItemRef}
        selected={selectedOption}
      />
    </S.AutocompleteWrapper>
  );
};

export default AutocompleteInput;
